import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { usePagination } from "@/hooks/use-pagination";
import { useToast } from "@/hooks/use-toast";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { videoService } from "@/lib/api/video.service";
import { useAuth } from "@/lib/auth";
import type {
  Category,
  Coach,
  StudioContentStatus,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Tags, User, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

const videoConfig = CONTENT_TYPE_CONFIG.video;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface VideoListItem {
  id: string;
  versionId: string;
  categoryId: string;
  categoryName: string | null;
  tagOptionIds: string[];
  status: StudioContentStatus;
  version: number;
  creator?: { id: string; name: string; roles: string[] };
  createdAt: string;
  updatedAt: string;
  primaryLanguage: string;
  primaryTitle: string;
  titles?: string[];
  languageCount: number;
  languageCodes: string[];
  coachIds: string[];
  coaches: Array<{ id: string; name: string | null; avatarUrl: string | null }>;
  tagCount: number;
}

export default function VideoListPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
    tag: "all",
    coach: "all",
  });
  const [languageFilter, setLanguageFilter] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: videoItems = [], isLoading } = useQuery<VideoListItem[]>({
    queryKey: ["/video"],
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  const { submitMutation, archiveMutation, withdrawMutation } =
    useContentLifecycle({
      contentType: "video",
      apiPath: "/video",
      listRoute: "/studio/video",
      editRoute: (id) => `/studio/video/${id}/edit`,
    });

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const restoreMutation = useMutation({
    mutationFn: (videoId: string) => videoService.restore(videoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/video"] });
      toast({ title: "Video restored to draft" });
    },
    onError: (err: any) => {
      toast({
        title: err?.message || "Failed to restore video",
        variant: "destructive",
      });
    },
  });

  const tagOptionsWithGroup = tagOptions.map((opt) => {
    const group = tagGroups.find((g) => g.id === opt.groupId);
    return { ...opt, groupName: group?.name ?? "" };
  });

  // ponytail: client-side filter — /video returns the full uncapped list, so filter here. Move to server query params if that endpoint ever paginates/caps.
  const filtered = videoItems.filter((item) => {
    if (
      searchQuery &&
      ![item.primaryTitle, ...(item.titles ?? [])].some((t) =>
        t.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    ) {
      return false;
    }
    if (filterValues.status !== "all" && item.status !== filterValues.status)
      return false;
    if (
      filterValues.category !== "all" &&
      item.categoryId !== filterValues.category
    )
      return false;
    if (
      filterValues.tag !== "all" &&
      !item.tagOptionIds.includes(filterValues.tag)
    )
      return false;
    if (
      filterValues.coach !== "all" &&
      !item.coachIds?.includes(filterValues.coach)
    )
      return false;
    if (
      languageFilter !== "all" &&
      !item.languageCodes?.includes(languageFilter)
    )
      return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) =>
    (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""),
  );
  const { pageItems, ...paginationProps } = usePagination(sorted);

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const customFilters = [
    {
      key: "category",
      label: "Categories",
      options: categories.map((cat) => ({ value: cat.id, label: cat.title })),
    },
    {
      key: "tag",
      label: "Tags",
      options: tagOptionsWithGroup.map((opt) => ({
        value: opt.id,
        label: opt.groupName ? `${opt.groupName}: ${opt.label}` : opt.label,
      })),
    },
    ...(isCoachOnly
      ? []
      : [
          {
            key: "coach",
            label: "Coaches",
            width: "160px",
            options: coaches.map((coach) => ({
              value: coach.id,
              label: coach.name,
            })),
          },
        ]),
  ];

  return (
    <ContentListShell
      contentTypeConfig={videoConfig}
      isLoading={isLoading}
      itemCount={filtered.length}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={true}
      languageFilterValue={languageFilter}
      onLanguageFilterChange={setLanguageFilter}
      pagination={{
        ...paginationProps,
        onPageChange: paginationProps.setPage,
        onNext: paginationProps.nextPage,
        onPrev: paginationProps.prevPage,
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Author</TableHead>
            <TableHead className="text-center">Tags</TableHead>
            <TableHead className="text-center">Languages</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageItems.map((item) => {
            const statusConfig = STATUS_CONFIG[item.status];
            return (
              <TableRow
                key={item.versionId}
                className="cursor-pointer"
                onClick={() => setLocation(`/studio/video/${item.id}/edit`)}
                data-testid={`row-video-${item.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <Video className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="font-medium text-foreground truncate"
                        data-testid={`text-video-title-${item.id}`}
                      >
                        {item.primaryTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {item.categoryName || "\u2014"}
                  </span>
                </TableCell>
                <TableCell>
                  {(() => {
                    const validCoaches =
                      item.coaches?.filter((c) => !!c.name) ?? [];
                    if (validCoaches.length === 0) {
                      return (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {"\u2014"}
                          </span>
                        </div>
                      );
                    }
                    const first = validCoaches[0];
                    const label =
                      validCoaches.length === 1
                        ? first.name
                        : `${first.name} +${validCoaches.length - 1}`;
                    return (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          {first.avatarUrl ? (
                            <AvatarImage
                              src={first.avatarUrl}
                              alt={first.name ?? ""}
                            />
                          ) : null}
                          <AvatarFallback className="text-[10px]">
                            {first.name?.charAt(0).toUpperCase() ?? "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-muted-foreground">
                          {label}
                        </span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.tagCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.languageCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusConfig.variant}
                    className="no-default-hover-elevate no-default-active-elevate"
                  >
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item.updatedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.creator?.id}
                    authorCoachId={
                      user && item.coachIds?.includes(user.id)
                        ? user.id
                        : undefined
                    }
                    onEdit={(id) => setLocation(`/studio/video/${id}/edit`)}
                    onSubmit={(id) => submitMutation.mutate(id)}
                    onArchive={(id) => archiveMutation.mutate(id)}
                    onWithdraw={(id) => withdrawMutation.mutate(id)}
                    onRestore={(id) => restoreMutation.mutate(id)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
